import React, { useEffect, useState } from 'react'
import { CompanyId, Lang, Role, UserCompanyDto } from '../../services/api.types'
import { Card } from '../../components/Card/Card'
import { t } from 'ttag'
import dayjs from '../../dayjsConfig'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import { currentLang } from '../../i18nInit'
import YumSmallSelect from '../../components/YumSmallSelect/YumSmallSelect'
import { Redirect } from 'react-router-dom'
import { yuMuuvPublicCompanyId } from '../Settings/Settings'
import YumHeader from '../../components/YumHeader/YumHeader'
import YumTabs from '../../components/YumTabs/YumTabs'
import YumTab from '../../components/YumTabs/YumTab/YumTab'
import DashboardWrapper from './DashboardWrapper'
import FunFactsWrapper from '../Statistics/FunFactsWrapper'
import CompanyRankingWrapper from '../CompanyRanking/CompanyRankingWrapper'
import { store } from '../../index'
import { push } from 'connected-react-router'
import { useHistory } from 'react-router'

export interface IStatisticsStateProps {
  tab: string | null
  userCompanies: UserCompanyDto[]
  selectedCompanyId: CompanyId | null
}

export interface IStatisticsDispatchProps {
  hasRoleInCompany(companyId: CompanyId, role: Role): boolean
  setSelectedCompanyId(payload: CompanyId): void
}

type IStatisticsProps = IStatisticsStateProps & IStatisticsDispatchProps

const getDefaultMonth = () => {
  const now = dayjs().utc().locale('en')
  const day = parseInt(now.format('D'))
  let month = day > 1 ? dayjs(now) : now.subtract(1, 'month')
  if (currentLang === Lang.EN) {
    month = month.locale('en')
  }
  return month
}

const getMonthsList = () => {
  let dateStart = dayjs('2023-01-01')
  if (currentLang === Lang.EN) {
    dateStart = dateStart.locale('en')
  }
  const dateEnd = getDefaultMonth()
  let interim = dayjs(dateStart)
  const timeValues: any = []

  while (dateEnd.isAfter(interim) || interim.format('M') === dateEnd.format('M')) {
    const interimLocalized = interim.locale('en')
    timeValues.push({
      value: interimLocalized.format('YYYY-MM'),
      label: interim.format('MMM YYYY').replace(/^\w/, (c) => c.toUpperCase()),
    })
    interim = interim.add(1, 'month')
  }

  return timeValues.reverse()
}

export const selectUserCompany = (companies: UserCompanyDto[], selectedCompanyId: CompanyId | null): UserCompanyDto => {
  if (selectedCompanyId) {
    const uc = companies.find((uc) => uc.companyId === selectedCompanyId)
    return uc ? uc : companies[0]
  }
  return companies[0]
}

enum StatisticsTabs {
  DASHBOARD = 'DASHBOARD',
  FUN_FACTS = 'FUN_FACTS',
  TOP_100 = 'TOP_100',
}

/*const getActiveTab = (
  tab: string | null,
  isDashboardVisible: boolean,
  isFunFactsVisible: boolean,
  isTop100Visible: boolean,
) => {
  switch (tab) {
    case 'fun-facts':
      return isFunFactsVisible ? StatisticsTabs.FUN_FACTS : null
    case 'top-100':
      return isTop100Visible ? StatisticsTabs.TOP_100 : null
    default:
      return isDashboardVisible ? StatisticsTabs.DASHBOARD : null
  }
}*/

const getActiveTab = (tab: string | null) => {
  switch (tab) {
    case 'fun-facts':
      return StatisticsTabs.FUN_FACTS
    case 'top-100':
      return StatisticsTabs.TOP_100
    default:
      return StatisticsTabs.DASHBOARD
  }
}

const getUserCompanies = (activeTab: StatisticsTabs, uc: UserCompanyDto[], hasRoleInCompany: any) => {
  switch (activeTab) {
    case StatisticsTabs.FUN_FACTS:
      return uc.filter(
        ({ companyId, hideStatisticsView }) => companyId !== yuMuuvPublicCompanyId && !hideStatisticsView,
      )
    case StatisticsTabs.TOP_100:
      return uc.filter(({ companyId, hideRankingView }) => companyId !== yuMuuvPublicCompanyId && !hideRankingView)
    default:
      return uc.filter((company) => {
        const adminAccess = company.reportRequired && hasRoleInCompany(company.companyId, Role.ADMIN)
        const userAccess = company.reportRequired && company.reportRequiredForUsers
        return company.companyId !== yuMuuvPublicCompanyId && (adminAccess || userAccess)
      })
  }
}

const getIsVisible = (activeTab: StatisticsTabs, uc: UserCompanyDto[], hasRoleInCompany?: any) => {
  if (!uc) return false
  switch (activeTab) {
    case StatisticsTabs.FUN_FACTS:
      return uc.some((uc) => !uc.hideStatisticsView)
    case StatisticsTabs.TOP_100:
      return uc.some((uc) => !uc.hideRankingView)
    default:
      return uc.some((company) => {
        const adminAccess = company.reportRequired && hasRoleInCompany(company.companyId, Role.ADMIN)
        const userAccess = company.reportRequired && company.reportRequiredForUsers
        return company.companyId !== yuMuuvPublicCompanyId && (adminAccess || userAccess)
      })
  }
}

export default function Statistics(props: IStatisticsProps) {
  const { tab, userCompanies: uc, selectedCompanyId, hasRoleInCompany, setSelectedCompanyId } = props
  const history = useHistory()

  if (!tab) {
    console.log('Redirect to /statistics/dashboard')
    return <Redirect to="/statistics/dashboard" />
  }

  const [monthsList, setMonthsList] = useState(getMonthsList())
  const [month, setMonth] = useState(getDefaultMonth().format('YYYY-MM'))

  const [isDashboardVisible, setIsDashboardVisible] = useState(false)
  const [isFunFactsVisible, setIsFunFactsVisible] = useState(false)
  const [isTop100Visible, setIsTop100Visible] = useState(false)

  /*const [isDashboardVisible, setIsDashboardVisible] = useState(
    getIsVisible(StatisticsTabs.DASHBOARD, uc, hasRoleInCompany),
  )
  const [isFunFactsVisible, setIsFunFactsVisible] = useState(getIsVisible(StatisticsTabs.FUN_FACTS, uc))
  const [isTop100Visible, setIsTop100Visible] = useState(getIsVisible(StatisticsTabs.TOP_100, uc))*/

  const [activeTab, setActiveTab] = useState(getActiveTab(tab))
  const [userCompanies, setUserCompanies] = useState(getUserCompanies(activeTab, uc, hasRoleInCompany))
  const [companyId, setCompanyId] = useState(selectUserCompany(userCompanies, selectedCompanyId)?.companyId)

  useEffect(() => {
    // console.log('url tab change', tab)
    setActiveTab(getActiveTab(tab))
  }, [tab])

  useEffect(() => {
    if (!userCompanies.some((uc) => uc.companyId === companyId)) {
      setCompanyId(userCompanies[0]?.companyId)
    }
  }, [companyId, userCompanies])

  useEffect(() => {
    setIsDashboardVisible(getIsVisible(StatisticsTabs.DASHBOARD, uc, hasRoleInCompany))
    setIsFunFactsVisible(getIsVisible(StatisticsTabs.FUN_FACTS, uc))
    setIsTop100Visible(getIsVisible(StatisticsTabs.TOP_100, uc))
  }, [uc])

  useEffect(() => {
    setUserCompanies(getUserCompanies(activeTab, uc, hasRoleInCompany))

    /*console.log('activeTab useEffect', activeTab)
    console.log('isDashboardVisible', isDashboardVisible)
    console.log('isFunFactsVisible', isFunFactsVisible)*/
    if (activeTab === StatisticsTabs.DASHBOARD && !isDashboardVisible && isFunFactsVisible) {
      console.log('Redirect to /statistics/fun-facts')
      //setActiveTab(StatisticsTabs.FUN_FACTS)
      history.push('/statistics/fun-facts')
      //return <Redirect to="/statistics/fun-facts" />
    }
    if (activeTab === StatisticsTabs.FUN_FACTS && !isFunFactsVisible && isTop100Visible) {
      console.log('Redirect to /statistics/top-100')
      //setActiveTab(StatisticsTabs.TOP_100)
      history.push('/statistics/top-100')
      //return <Redirect to="/statistics/top-100" />
    }
    if (activeTab === StatisticsTabs.TOP_100 && !isTop100Visible && isDashboardVisible) {
      console.log('Redirect to /statistics/dashboard')
      //setActiveTab(StatisticsTabs.DASHBOARD)
      history.push('/statistics/dashboard')
      //return <Redirect to="/statistics/dashboard" />
    }
  }, [activeTab, isDashboardVisible, isTop100Visible, isFunFactsVisible])

  /*useEffect(() => {
    setUserCompanies(getUserCompanies(activeTab, uc, hasRoleInCompany))
    setIsDashboardVisible(getIsVisible(StatisticsTabs.DASHBOARD, uc, hasRoleInCompany))
    setIsFunFactsVisible(getIsVisible(StatisticsTabs.FUN_FACTS, uc))
    setIsTop100Visible(getIsVisible(StatisticsTabs.TOP_100, uc))
  }, [uc, activeTab])*/

  const handleMonthChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setMonth(event.target.value as string)
  }

  const handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string
    setCompanyId(value)
    setSelectedCompanyId(value)
  }

  const redirectTab = (activeTab: StatisticsTabs) => {
    const tab =
      activeTab == StatisticsTabs.FUN_FACTS
        ? 'fun-facts'
        : activeTab == StatisticsTabs.TOP_100
          ? 'top-100'
          : 'dashboard'
    store.dispatch(push(`/statistics/${tab}`))
  }

  const handleTabChange = (event: React.ChangeEvent<{}>, tab: StatisticsTabs) => {
    //setActiveTab(tab)
    redirectTab(tab)
  }

  const renderContent = () => {
    switch (activeTab) {
      case StatisticsTabs.DASHBOARD:
        return isDashboardVisible && <DashboardWrapper companyId={companyId} month={month} />
      case StatisticsTabs.FUN_FACTS:
        return isFunFactsVisible && <FunFactsWrapper companyId={companyId} />
      case StatisticsTabs.TOP_100:
        return isTop100Visible && <CompanyRankingWrapper companyId={companyId} month={month} />
    }
    return ''
  }

  const isCompletelyDisabled = !isDashboardVisible && !isFunFactsVisible && !isTop100Visible

  return (
    <Card>
      <NotificationsWrapper />
      <YumHeader>
        <h1>{t`Statistics`}</h1>
        {!isCompletelyDisabled && userCompanies.length > 1 && (
          <YumSmallSelect variant="outlined" value={companyId} onChange={handleCompanyChange}>
            {userCompanies.map((userCompany: UserCompanyDto, keyIndex: number) => (
              <option key={keyIndex} value={userCompany.companyId}>
                {userCompany.companyName}
              </option>
            ))}
          </YumSmallSelect>
        )}
        {!isCompletelyDisabled && activeTab != StatisticsTabs.FUN_FACTS && (
          <YumSmallSelect value={month} onChange={handleMonthChange}>
            {monthsList.map((month: any, keyIndex: number) => (
              <option key={keyIndex} value={month.value}>
                {month.label}
              </option>
            ))}
          </YumSmallSelect>
        )}
      </YumHeader>
      {isCompletelyDisabled ? (
        <p style={{ opacity: 0.5 }}>{t`Statistics view is disabled for this company.`}</p>
      ) : (
        <>
          <YumTabs
            onChange={handleTabChange}
            value={activeTab}
            style={{ marginBottom: '20px', backgroundColor: '#F5F5F9' }}
          >
            {isDashboardVisible && <YumTab label={t`Statistics`} value={StatisticsTabs.DASHBOARD} />}
            {isFunFactsVisible && <YumTab label={t`Fun facts`} value={StatisticsTabs.FUN_FACTS} />}
            {isTop100Visible && <YumTab label={t`TOP 100`} value={StatisticsTabs.TOP_100} />}
          </YumTabs>
          {renderContent()}
        </>
      )}
    </Card>
  )
}
